import * as React from 'react'

import { CreditOffer } from 'embed/constants/models'

import SortOptions from 'embed/components/SortOptions'

import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import { GenericCta } from 'embed/components/GenericCta'
import { CreditOfferListItem } from './ListItem/list-item'

import classnames from 'classnames'
import { CCIntroVsPreviewVsHybridOfferList, withExperiment } from 'embed/experiments'

import {
  CreditCardFlow,
  CreditCardStep,
  CreditSortOptionsWithRewardsUIValues,
  CreditSortOptionUI,
  CreditSortOptionUIValues,
  Product,
  SortDirection,
  TrackingKey,
} from 'even-ts-static'
import { NoPreviewOffersMessage } from '../../NoPreviewOffersMessage'

const CreditCardImage = require('./credit-card.svg')

const resultsStyles = require('embed/styles/results')

export interface Props {
  allCreditCardOffers?: CreditOffer[]
  applyNow?: boolean
  creditOffers: CreditOffer[]
  dispatchCreditCardOfferCta: (offer: CreditOffer) => void
  dispatchCreditCardOfferCtaSecondary?: (offer: CreditOffer) => void
  flow?: CreditCardFlow
  genericOfferIndex?: number
  leadUuid?: string
  offerListRef?: React.Ref<HTMLOListElement>
  onSortOptionClick: (sortOption: CreditSortOptionUI) => void
  toggleSpendingProfile?: (visible: boolean) => void
  showRewardsInfo?: boolean
  rateTableUuid?: string
  rewardsEnabled?: boolean
  rewardsPreference?: string
  hideSorting?: boolean
  selectedVariantId?: string
  sort: CreditSortOptionUI
  sortDirection: SortDirection
  step?: CreditCardStep
}

// eslint-disable-next-line complexity
export const CreditOfferListComponent: React.SFC<Props> = ({
  allCreditCardOffers,
  applyNow,
  creditOffers,
  dispatchCreditCardOfferCta,
  dispatchCreditCardOfferCtaSecondary,
  flow,
  genericOfferIndex,
  leadUuid,
  offerListRef,
  onSortOptionClick,
  toggleSpendingProfile,
  rateTableUuid,
  rewardsEnabled,
  showRewardsInfo,
  hideSorting,
  selectedVariantId,
  sort,
  sortDirection,
  step,
}) => {
  const renderedOffers = (
    <ol className={resultsStyles.list} ref={offerListRef}>
      {creditOffers.map((offer) => (
        <CreditOfferListItem
          applyNow={applyNow}
          dispatchCreditCardOfferCta={dispatchCreditCardOfferCta.bind(null, offer)}
          dispatchCreditCardOfferCtaSecondary={dispatchCreditCardOfferCtaSecondary}
          flow={flow}
          key={offer.uuid}
          leadUuid={leadUuid}
          offer={offer}
          toggleSpendingProfile={toggleSpendingProfile}
          rateTableUuid={rateTableUuid}
          rewardsEnabled={rewardsEnabled}
          step={step}
        />
      ))}
    </ol>
  )

  // Add the generic CTA at the specified index but don't show if we're on the results step
  const showCta =
    genericOfferIndex !== undefined &&
    genericOfferIndex !== -1 &&
    step !== CreditCardStep.Results &&
    !selectedVariantId &&
    creditOffers.length

  // Which sort options to show
  const sortOptionValues =
    rewardsEnabled &&
    showRewardsInfo &&
    allCreditCardOffers?.some((offer) => offer.details.earningProgram?.personalization?.totalValue) &&
    step === CreditCardStep.Results
      ? CreditSortOptionsWithRewardsUIValues
      : CreditSortOptionUIValues

  return (
    <React.Fragment>
      <NoPreviewOffersMessage offers={creditOffers} product={Product.CreditCard} />
      <div className={resultsStyles.creditPreviewDisclosureContainer}>
        <div>
          {creditOffers.length > 0 && !hideSorting && (
            <SortOptions
              onSortOptionClick={onSortOptionClick}
              sort={sort}
              sortDirection={sortDirection}
              sortOptionValues={sortOptionValues}
            />
          )}
        </div>
        <AdvertiserDisclosure
          className={classnames(resultsStyles.sortBarDisclosure, resultsStyles.creditPreviewDisclosure)}
        />
      </div>
      {selectedVariantId && (
        <GenericCta
          buttonText="Get Started"
          contentText="We crunch the numbers to match you with card offers based on your spending habits, preferences, and credit profile"
          image={CreditCardImage}
          isHybridStep={true}
          selectedStepOnClick={CreditCardStep.PreSelectForm}
          titleText="Get Matched With Personalized Credit Card Offers"
          trackingEvent={TrackingKey.GenericCreditOfferClick}
        />
      )}
      {showCta && (
        <GenericCta
          buttonText="Get Started"
          contentText="Answer a few questions and we will match you with the top offers you may be pre-qualified or pre-approved for."
          image={CreditCardImage}
          selectedStepOnClick={CreditCardStep.PreApprovalForm}
          titleText="Get matched with personalized credit card offers"
          trackingEvent={TrackingKey.GenericCreditOfferClick}
        />
      )}
      {renderedOffers}
    </React.Fragment>
  )
}

export const CreditOfferList = withExperiment(CCIntroVsPreviewVsHybridOfferList)(CreditOfferListComponent)
